import React, { useState } from 'react';
import colors from '../../theme';

const FilterPanel = ({
  tags,
  nestedTags = [],
  lands,
  selectedTag,
  onTagFilter,
  onClearFilters,
  getLandsByTag,
  t,
  effectiveSearchQuery,
  selectedTags = [],
  onTagSelect,
  landTypes = [],
  selectedLandType,
  onLandTypeFilter,
  language = 'ar',
  isAuthenticated = false
}) => {
  const [expandedParents, setExpandedParents] = useState(new Set());

  const toggleExpanded = (tagId) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  };

  // Build the parent -> children tree from the flat nesting relationships.
  const childTagIds = new Set(nestedTags.map((n) => n.child_tag_id));
  const childrenByParent = {};
  nestedTags.forEach((n) => {
    if (!childrenByParent[n.parent_tag_id]) childrenByParent[n.parent_tag_id] = [];
    const childTag = tags.find((tag) => tag.id === n.child_tag_id);
    if (childTag) childrenByParent[n.parent_tag_id].push(childTag);
  });

  // Top-level rows: any tag that isn't itself nested under another tag.
  const topLevelTags = tags.filter((tag) => tag.title !== 'All' && !childTagIds.has(tag.id));

  return (
    <div dir="rtl" className="h-full flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-lg font-semibold text-gray-900 text-right">
          {t('الفلاتر', 'Filters')}
        </h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Tags List */}
        <div>
          <ul className="space-y-2">
            {/* All Properties Option */}
            <li>
              <button
                onClick={() => onTagFilter(null)}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  selectedTag === null
                    ? 'bg-yellow-50 border-2 border-yellow-600'
                    : 'bg-white border-2 border-transparent hover:bg-gray-50'
                }`}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: colors.primaryBrown }}
                />
                <span className="flex-1 text-right text-sm font-medium text-gray-900">
                  {t('جميع العقارات', 'All Properties')}
                </span>
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                  {lands.length}
                </span>
              </button>
            </li>

            {/* Tag Options — flat tags render as before; tags with children
                render collapsed with an expand arrow. */}
            {topLevelTags.map((tag) => {
              const children = childrenByParent[tag.id];
              const hasChildren = children && children.length > 0;
              const isExpanded = expandedParents.has(tag.id);
              const isParentSelected = selectedTag?.id === tag.id;

              return (
                <li key={tag.id}>
                  <div
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      isParentSelected
                        ? 'bg-yellow-50 border-2 border-yellow-600'
                        : 'bg-white border-2 border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <button
                      onClick={() =>
                        onTagFilter(hasChildren ? { ...tag, childTagIds: children.map((c) => c.id) } : tag)
                      }
                      className="flex-1 flex items-center gap-3 text-right"
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="flex-1 text-right text-sm font-medium text-gray-900">
                        {tag.title}
                      </span>
                      <span
                        className="px-2 py-1 text-xs font-medium rounded-full text-white"
                        style={{ backgroundColor: tag.color }}
                      >
                        {getLandsByTag(tag.id, hasChildren ? children.map((c) => c.id) : undefined)}
                      </span>
                    </button>

                    {hasChildren && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpanded(tag.id);
                        }}
                        className="flex-shrink-0 p-1 text-gray-500 hover:text-gray-800"
                        aria-label="toggle children"
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {hasChildren && isExpanded && (
                    <ul className="mt-2 mr-6 space-y-2 border-r-2 border-gray-200 pr-3">
                      {children.map((child) => (
                        <li key={child.id}>
                          <button
                            onClick={() => onTagFilter(child)}
                            className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                              selectedTag?.id === child.id
                                ? 'bg-yellow-50 border-2 border-yellow-600'
                                : 'bg-white border-2 border-transparent hover:bg-gray-50'
                            }`}
                          >
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: child.color }}
                            />
                            <span className="flex-1 text-right text-sm font-medium text-gray-900">
                              {child.title}
                            </span>
                            <span
                              className="px-2 py-1 text-xs font-medium rounded-full text-white"
                              style={{ backgroundColor: child.color }}
                            >
                              {getLandsByTag(child.id)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Land Type Filter - Only show if authenticated */}
        {isAuthenticated && (
          <div className="space-y-3">
            <h3 className="text-base font-semibold text-gray-900 text-right">
              {t('نوع العقار', 'Property Type')}
            </h3>

            <div className="space-y-2">
              {[
                { value: null, label: t('جميع الأنواع', 'All Types') },
                ...landTypes.map(type => ({
                  value: type,
                  label: language === 'ar' ? type.display_name_ar : type.display_name_en
                }))
              ].map((type) => (
                <button
                  key={type.value?.name || 'all'}
                  onClick={() => onLandTypeFilter(type.value)}
                  className={`w-full px-4 py-2 text-sm font-medium rounded-lg border-2 transition-colors text-right ${
                    selectedLandType?.name === type.value?.name
                      ? 'bg-primary border-primary text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Multi-Tag Selection */}
        <div className="space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">
                {t('أو', 'OR')}
              </span>
            </div>
          </div>

          <h3 className="text-base font-semibold text-gray-900 text-right">
            {t('تحديد متعدد (الكل)', 'Multi-Select (ALL)')}
          </h3>

          <div className="flex flex-wrap gap-2 justify-end">
            {tags.filter(tag => tag.title !== 'All').map((tag) => (
              <button
                key={`multi-${tag.id}`}
                onClick={() => onTagSelect && onTagSelect(tag.title)}
                className={`px-3 py-1 text-xs font-medium rounded-full border-2 transition-colors ${
                  selectedTags.includes(tag.title)
                    ? 'text-white'
                    : 'bg-opacity-10'
                }`}
                style={{
                  backgroundColor: selectedTags.includes(tag.title) ? tag.color : `${tag.color}20`,
                  borderColor: tag.color,
                  color: selectedTags.includes(tag.title) ? 'white' : tag.color
                }}
              >
                {tag.title}
              </button>
            ))}
          </div>

          {selectedTags.length > 0 && (
            <p className="text-xs text-gray-600 mt-2 text-right">
              {t(
                `العقارات التي تحتوي على جميع التصنيفات: ${selectedTags.join(', ')}`,
                `Properties containing ALL tags: ${selectedTags.join(', ')}`
              )}
            </p>
          )}
        </div>

        {/* Active Filters Display */}
        {(selectedTag || selectedTags.length > 0 || effectiveSearchQuery || selectedLandType) && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-right">
            <p className="text-xs text-gray-600 mb-1">
              {t('النتائج', 'Results')}
            </p>
            <p className="text-2xl font-bold text-gray-900">
              {lands.length} <span className="text-base font-normal">{t('عقار', 'Properties')}</span>
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <button
          onClick={onClearFilters}
          className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {t('مسح الفلاتر', 'Clear Filters')}
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;
